import { Check, Loader2, RefreshCw, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { telegramService, type TelegramStatus } from '../../services/telegram.service';
import { Button } from '../common/Button';

/** Định dạng mốc liên kết; chuỗi rỗng nếu thiếu/không đọc được. */
function formatLinkedAt(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('vi-VN');
}

/** Nhịp hỏi trạng thái khi đang chờ người dùng nhập mã cho bot. */
const LINK_POLL_MS = 3_000;

/**
 * Liên kết Telegram: tạo MÃ gắn với chính tài khoản này, rồi đưa mã cho bot.
 *
 * Mã sinh ở WEB (gắn userId) chứ không phải ở bot — nên chỉ tài khoản này mới
 * liên kết được bằng mã đó. Người dùng gõ `/connect <mã>` cho bot (hoặc bấm nút
 * mở bot), trang này POLL trạng thái tới khi liên kết xong.
 *
 * Toast (thành công/lỗi) do interceptor `api.ts` lo — ở đây chỉ tải lại trạng thái.
 */
export function TelegramLinkCard() {
  const [status, setStatus] = useState<TelegramStatus | null>(null);
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Tải trạng thái + thông tin bot ĐỘC LẬP: `allSettled` để một call hỏng không
   * nuốt luôn call kia. Chỉ coi là "tải hỏng" khi KHÔNG lấy được trạng thái —
   * đó là thứ quyết định giao diện; thiếu username chỉ mất cái link t.me.
   */
  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    const [statusRes, botRes] = await Promise.allSettled([
      telegramService.getStatus(),
      telegramService.getBotInfo(),
    ]);
    if (statusRes.status === 'fulfilled') setStatus(statusRes.value);
    else setLoadFailed(true);
    if (botRes.status === 'fulfilled') setBotUsername(botRes.value.username);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Trong lúc chờ người dùng đưa mã cho bot, hỏi trạng thái đều đặn; liên kết
  // xong thì dừng poll và ẩn mã.
  useEffect(() => {
    if (!activeCode) return;
    let alive = true;
    const timer = setInterval(() => {
      void telegramService
        .getStatus()
        .then((next) => {
          if (!alive) return;
          setStatus(next);
          if (next.linked) setActiveCode(null);
        })
        .catch(() => undefined);
    }, LINK_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [activeCode]);

  const createCode = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { code } = await telegramService.createCode();
      setActiveCode(code);
    } catch {
      // interceptor đã toast.
    } finally {
      setBusy(false);
    }
  };

  const submitUnlink = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await telegramService.unlink();
      const next = await telegramService.getStatus();
      setStatus(next);
      setActiveCode(null);
    } catch {
      // interceptor đã toast.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-line bg-panel rounded-xl border p-6">
      <div className="mb-4 flex items-center gap-2">
        <Send size={18} className="text-accent" />
        <h2 className="text-ink text-[15px] font-bold">Telegram</h2>
        {busy && <Loader2 size={14} className="text-accent animate-spin" />}
      </div>

      {loading ? (
        <div className="text-ink-dim flex items-center gap-2 py-4 text-[12px]">
          <Loader2 size={14} className="animate-spin" />
          Đang tải trạng thái...
        </div>
      ) : !status ? (
        <div className="flex items-center justify-between gap-4 py-2">
          <span className="text-ink-dim text-[12px]">
            {loadFailed ? 'Không tải được trạng thái liên kết.' : 'Chưa có trạng thái.'}
          </span>
          <Button variant="secondary" onClick={() => void loadAll()}>
            <RefreshCw size={14} className="mr-1" />
            Thử lại
          </Button>
        </div>
      ) : status.linked ? (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Check size={16} className="text-success" />
            <div>
              <span className="text-ink block text-[13px] font-bold">Đã liên kết</span>
              {formatLinkedAt(status.linkedAt) && (
                <span className="text-ink-dim mt-0.5 block text-[11px]">
                  Từ {formatLinkedAt(status.linkedAt)}
                </span>
              )}
            </div>
          </div>
          <Button variant="danger" loading={busy} onClick={submitUnlink}>
            Huỷ liên kết
          </Button>
        </div>
      ) : botUsername ? (
        <div className="space-y-4">
          {activeCode ? (
            <div className="space-y-3">
              <div className="border-line bg-panel-hover rounded-lg border p-4 text-center">
                <span className="text-ink-dim block text-[11px]">Mã liên kết của bạn (5 phút)</span>
                <span className="text-ink mt-1 block font-mono text-[22px] font-bold tracking-[0.3em]">
                  {activeCode}
                </span>
              </div>
              <ol className="text-ink-dim list-decimal space-y-1 pl-5 text-[12px]">
                <li>
                  Gõ cho bot{' '}
                  <span className="text-ink font-mono font-bold">/connect {activeCode}</span>
                </li>
                <li>Hoặc bấm nút dưới để mở bot và liên kết tự động</li>
              </ol>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() =>
                    window.open(
                      `https://t.me/${botUsername}?start=${activeCode}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  Mở bot @{botUsername}
                </Button>
                <span className="text-ink-dim flex items-center gap-1.5 text-[12px]">
                  <Loader2 size={12} className="animate-spin" />
                  Đang chờ liên kết…
                </span>
              </div>
              <button
                type="button"
                className="text-ink-dim text-[11px] hover:underline"
                onClick={() => void createCode()}
              >
                Tạo mã khác
              </button>
            </div>
          ) : (
            <>
              <ol className="text-ink-dim list-decimal space-y-1 pl-5 text-[12px]">
                <li>Bấm “Tạo mã liên kết” để lấy mã 6 ký tự gắn với tài khoản này</li>
                <li>
                  Mở bot{' '}
                  <a
                    href={`https://t.me/${botUsername}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent font-bold hover:underline"
                  >
                    @{botUsername}
                  </a>{' '}
                  và gõ <span className="text-ink font-mono font-bold">/connect &lt;mã&gt;</span>
                </li>
              </ol>
              <Button loading={busy} onClick={() => void createCode()}>
                Tạo mã liên kết
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="text-ink-dim py-2 text-[12px]">
          Bot Telegram chưa được cấu hình. Liên hệ quản trị viên để bật tính năng thông báo.
        </div>
      )}
    </div>
  );
}
